import { ArrowRight, Car, Zap, Truck, Bike, CalendarCheck, UserCheck, ClipboardCheck, Star, Users, FileText, ShieldCheck, Instagram, Youtube, Facebook, Linkedin, Check } from "lucide-react";
import { Button } from "../ui/button";
import Logo from "../Logo";
import { useLang } from "../../context/LanguageContext";
import { vehicleTypes, howItWorks, landingStats, testimonials, IMAGES } from "../../mock";

const STEP_ICONS = [Car, UserCheck, CalendarCheck, ClipboardCheck, Star];
const STAT_ICONS = [Users, Star, FileText, ShieldCheck];
const TYPE_ICONS = { bekas: Car, listrik: Zap, truk: Truck, moge: Bike };

function SectionHeader({ title, accent, sub, link }) {
  return (
    <div className="flex items-end justify-between gap-4 mb-8">
      <div>
        <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">
          {title} {accent && <span className="text-blue-600">{accent}</span>}
        </h2>
        {sub && <p className="text-sm text-slate-500 mt-1.5">{sub}</p>}
      </div>
      {link && (
        <a href="#" className="hidden sm:flex items-center gap-1 text-sm font-semibold text-blue-600 hover:text-blue-700 whitespace-nowrap">
          {link} <ArrowRight size={15} />
        </a>
      )}
    </div>
  );
}

function VehicleTypes() {
  const { t } = useLang();
  return (
    <section id="layanan" className="max-w-7xl mx-auto px-4 sm:px-6 py-14">
      <SectionHeader title={t("services.title")} sub={t("services.sub")} link={t("services.link")} />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {vehicleTypes.map((v) => {
          const Icon = TYPE_ICONS[v.id];
          return (
            <div key={v.id} className="group bg-white rounded-2xl border border-slate-100 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 overflow-hidden cursor-pointer">
              <div className="h-44 overflow-hidden bg-slate-50">
                <img src={v.img} alt={t(`vehicle.${v.id}`)} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              </div>
              <div className="p-4 flex items-start justify-between gap-2">
                <div>
                  <p className="font-bold text-slate-900 flex items-center gap-2"><Icon size={16} className="text-blue-600" />{t(`vehicle.${v.id}`)}</p>
                  <p className="text-xs text-slate-500 mt-1 leading-relaxed">{t(`services.items.${v.id}`)}</p>
                </div>
                <span className="mt-1 text-slate-300 group-hover:text-blue-600 group-hover:translate-x-1 transition-all"><ArrowRight size={18} /></span>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function HowItWorks() {
  const { t } = useLang();
  const steps = t("how.steps");
  return (
    <section id="cara-kerja" className="max-w-7xl mx-auto px-4 sm:px-6 py-14">
      <SectionHeader title={t("how.title")} accent={t("how.accent")} sub={t("how.sub")} link={t("how.link")} />
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-y-10 gap-x-4">
        {steps.map((s, i) => {
          const Icon = STEP_ICONS[i];
          return (
            <div key={i} className="relative text-center px-2">
              <div className="flex items-center justify-center gap-3 mb-4">
                <span className="w-9 h-9 rounded-full bg-blue-600 text-white font-bold text-sm flex items-center justify-center shadow-lg shadow-blue-600/30">{i + 1}</span>
                <span className="w-14 h-14 rounded-2xl bg-white border border-slate-100 shadow-md flex items-center justify-center"><Icon size={26} className="text-slate-800" /></span>
              </div>
              {i < steps.length - 1 && <ArrowRight size={18} className="hidden lg:block absolute top-6 -right-3 text-slate-300" />}
              <p className="font-bold text-sm text-slate-900">{s.title}</p>
              <p className="text-xs text-slate-500 mt-1.5 leading-relaxed">{s.desc}</p>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function CtaBanner({ onCta }) {
  const { t } = useLang();
  return (
    <section id="harga" className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
      <div className="relative rounded-3xl bg-gradient-to-r from-blue-50 via-slate-50 to-white border border-blue-100 overflow-hidden grid lg:grid-cols-2">
        <div className="p-8 sm:p-12 relative z-10">
          <h3 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight leading-tight">{t("cta.title1")}<br />{t("cta.title2")}</h3>
          <p className="text-sm text-slate-500 mt-4 max-w-sm leading-relaxed">{t("cta.desc")}</p>
          <Button onClick={onCta} className="mt-6 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl px-6 h-12 shadow-lg shadow-blue-600/25">
            {t("cta.button")} <ArrowRight size={16} className="ml-1.5" />
          </Button>
          <div className="grid grid-cols-2 gap-3 mt-8 max-w-xs">
            {t("cta.points").map((p) => (
              <p key={p} className="flex items-center gap-2 text-xs font-semibold text-slate-700">
                <span className="bg-blue-600 rounded-full p-1"><Check size={10} className="text-white" /></span> {p}
              </p>
            ))}
          </div>
        </div>
        <div className="relative h-64 lg:h-auto">
          <img src={IMAGES.ctaCouple} alt="Inspeksi mobil" loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-blue-50/80 via-transparent to-transparent" />
          <p className="absolute top-6 right-6 font-hand text-2xl sm:text-3xl text-slate-900 rotate-2 text-right leading-tight bg-white/70 rounded-xl px-3 py-1 backdrop-blur-sm">
            {t("cta.hand1")}<br />{t("cta.hand2")}<br />{t("cta.hand3")}<br />{t("cta.hand4")}
          </p>
        </div>
      </div>
    </section>
  );
}

function Stats() {
  const { t } = useLang();
  const labels = t("stats.items");
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 py-12">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
        {landingStats.map((s, i) => {
          const Icon = STAT_ICONS[i];
          return (
            <div key={i} className="flex items-center gap-4 justify-center">
              <span className="w-12 h-12 rounded-2xl bg-blue-50 flex items-center justify-center shrink-0"><Icon size={24} className="text-blue-600" /></span>
              <div>
                <p className="text-xl sm:text-2xl font-extrabold text-slate-900">{s.value}</p>
                <p className="text-xs text-slate-500 font-medium">{labels[i]}</p>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function Testimonials() {
  const { t } = useLang();
  const items = t("testi.items");
  return (
    <section id="tentang-kami" className="max-w-7xl mx-auto px-4 sm:px-6 py-14">
      <SectionHeader title={t("testi.title")} sub={t("testi.sub")} link={t("testi.link")} />
      <div className="grid md:grid-cols-3 gap-5">
        {testimonials.map((tm, i) => (
          <div key={tm.name} className="bg-white rounded-2xl border border-slate-100 shadow-sm hover:shadow-lg transition-shadow p-6">
            <div className="flex items-center gap-3 mb-4">
              <img src={tm.avatar} alt={tm.name} loading="lazy" className="w-11 h-11 rounded-full object-cover" />
              <span className="text-amber-400 text-sm tracking-tight">★★★★★</span>
            </div>
            <p className="text-sm text-slate-600 leading-relaxed">"{items[i]}"</p>
            <p className="text-xs font-bold text-slate-900 mt-4">{tm.name}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

export function Footer() {
  const { t } = useLang();
  const socials = [Instagram, Youtube, Facebook, Linkedin];
  return (
    <footer className="bg-[#0b1736] text-white mt-10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10 flex flex-col md:flex-row items-center justify-between gap-6">
        <Logo dark />
        <nav className="flex flex-wrap justify-center gap-x-7 gap-y-2">
          {t("footer.links").map((l, i) => <a key={i} href="#" className="text-sm text-slate-300 hover:text-white transition-colors">{l}</a>)}
        </nav>
        <div className="flex items-center gap-4">
          {socials.map((Icon, i) => <a key={i} href="#" className="text-slate-400 hover:text-white transition-colors"><Icon size={18} /></a>)}
        </div>
      </div>
      <div className="border-t border-white/10">
        <p className="max-w-7xl mx-auto px-4 sm:px-6 py-4 text-right text-xs text-slate-400">{t("footer.copyright")}</p>
      </div>
    </footer>
  );
}

export function LandingSections({ onCta }) {
  return (
    <>
      <VehicleTypes />
      <HowItWorks />
      <CtaBanner onCta={onCta} />
      <Stats />
      <Testimonials />
      <Footer />
    </>
  );
}
